import { PhraseHighlight } from '@/components/displays/PhraseHighlight';
import { Result, SlideComponentHkcsQ1 } from '@/types/slide';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageShowcase } from '../../ImageShowcase';
import { PropsWithOnLoad } from '../../type';
import { ResultContentContainer } from './ResultContentContainer';

type HkcsQ1Answer = {
    hkcsQ1: {
        selectedIndex: number;
    };
};

const isHkcsQ1Answer = (answer: unknown): answer is HkcsQ1Answer => {
    return (
        typeof answer === 'object' &&
        answer !== null &&
        'hkcsQ1' in answer &&
        typeof (answer as HkcsQ1Answer).hkcsQ1 === 'object' &&
        (answer as HkcsQ1Answer).hkcsQ1 !== null &&
        'selectedIndex' in (answer as HkcsQ1Answer).hkcsQ1 &&
        typeof (answer as HkcsQ1Answer).hkcsQ1.selectedIndex === 'number'
    );
};

export const ResultContentHkcsQ1: React.FC<
    PropsWithOnLoad & {
        question: SlideComponentHkcsQ1 & { answers: Result[] };
    }
> = ({ question, onLoad }) => {
    const { t } = useTranslation();

    const answer = question.answers[0];
    const data = answer?.data ? JSON.parse(answer.data) : undefined;

    const selectedImageIndex = isHkcsQ1Answer(data) ? data.hkcsQ1.selectedIndex : undefined;
    const selectedImageSrc =
        selectedImageIndex !== undefined
            ? question.content.questions[selectedImageIndex]?.imageURL ||
              `/asset/characters/${question.content.questions[selectedImageIndex]?.image ? question.content.questions[selectedImageIndex]?.image + '/image.png' : 'default.png'}`
            : undefined;

    const [loadCount, setLoadCount] = useState<number>(
        (question.content.image1URL || question.content.image1 ? 1 : 0) +
            (question.content.image2URL || question.content.image2 ? 1 : 0) +
            question.content.questions.length +
            (selectedImageSrc ? 1 : 0),
    );

    const handleLoadImage = useCallback(() => {
        setLoadCount((prev) => {
            return prev - 1;
        });
    }, []);

    useEffect(() => {
        if (loadCount <= 0) {
            onLoad?.();
        }
    }, [loadCount, onLoad]);

    return (
        <ResultContentContainer>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', py: 1 }}>
                    <PhraseHighlight sx={{ color: '#555', my: 2 }} phrase={question.content.phrase} highlight={question.content.phrase} underline={false} />
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            p: 1,
                            justifyContent: 'center',
                        }}
                    >
                        <ImageShowcase
                            src={question.content.image1URL ? question.content.image1URL : `/asset/characters/${question.content.image1 ? question.content.image1 + '/image.png' : 'default.png'}`}
                            keyword={question.content.keyword1}
                            width={150}
                            onLoad={handleLoadImage}
                        />
                        <ImageShowcase
                            src={question.content.image2URL ? question.content.image2URL : `/asset/characters/${question.content.image2 ? question.content.image2 + '/image.png' : 'default.png'}`}
                            keyword={question.content.keyword2}
                            width={150}
                            onLoad={handleLoadImage}
                        />
                    </Box>
                    <Box sx={{ flex: 1, width: 480, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <PhraseHighlight
                            phrase={t('result.message.hkcsQ1.description', { phrase: question.content.phrase })}
                            highlight={question.content.phrase}
                            underline={false}
                            sx={{ color: '#555', fontSize: 20, my: 1 }}
                            gutterBottom
                        />
                        <Box sx={{ position: 'relative', width: '100%', flex: 1, justifyItems: 'center' }}>
                            <Box
                                sx={{
                                    width: '60%',
                                    height: '100%',
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    border: 2,
                                    borderColor: 'divider',
                                    justifyContent: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {selectedImageSrc && <ImageShowcase src={selectedImageSrc} width={132} highlight onLoad={handleLoadImage} />}
                            </Box>
                            <DoubleArrowIcon sx={{ color: '#555', position: 'absolute', right: 24, top: 0, bottom: 0, margin: 'auto 0', transform: 'scaleX(-1)', fontSize: 48 }} />
                        </Box>
                    </Box>
                </Box>
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1,
                        gap: 1,
                        borderRadius: 1,
                        backgroundColor: (theme.vars || theme).palette.grey[500],
                    })}
                >
                    {question.content.questions.map((q, index) => (
                        <ImageShowcase
                            key={index}
                            src={q.imageURL && q.imageURL !== '' ? q.imageURL : `/asset/characters/${q.image ? q.image + '/image.png' : 'default.png'}`}
                            width={106}
                            highlight={selectedImageIndex === index}
                            onLoad={handleLoadImage}
                        />
                    ))}
                </Box>
            </Box>
        </ResultContentContainer>
    );
};
